import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { format } from 'date-fns';
import { memo, useCallback, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { useParams } from 'react-router-dom';
import { UAParser } from 'ua-parser-js';

import { Badge } from '@doger/ui/components/ui/badge';
import { Button } from '@doger/ui/components/ui/button';
import { Skeleton } from '@doger/ui/components/ui/skeleton';

import type { SessionDto } from '@doger/admin/shared/api';
import { $$privateApi } from '@doger/admin/shared/api';

import styles from './user-sessions.module.scss';

const SESSIONS_PER_PAGE = 5;

export const UserSessionsPage = memo(() => {
  const userId = useParams<{ userId: string }>().userId!;
  const queryClient = useQueryClient();

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
  } = useInfiniteQuery({
    queryKey: ['user-sessions', userId],
    queryFn: ({ pageParam = 0 }) =>
      $$privateApi.getUserSessions({
        userId,
        take: SESSIONS_PER_PAGE,
        skip: pageParam * SESSIONS_PER_PAGE,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.sessions.length === SESSIONS_PER_PAGE
        ? allPages.length
        : undefined,
  });

  const endAllSessionsMutation = useMutation({
    mutationFn: () => $$privateApi.endAllUserSessions(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['user-sessions', userId],
      });
    },
  });

  const { ref, inView } = useInView({
    threshold: 0,
  });

  const loadMore = useCallback(() => {
    if (inView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  useEffect(() => {
    loadMore();
  }, [loadMore]);

  const allSessions = useMemo(
    () => data?.pages.flatMap((page) => page.sessions) ?? [],
    [data]
  );

  const columns = useMemo<ColumnDef<SessionDto>[]>(
    () => [
      {
        accessorKey: 'createdAt',
        header: 'Created',
        cell: ({ row }) => (
          <div className={styles.dateInfo}>
            {format(new Date(row.original.createdAt), 'MMM dd, yyyy HH:mm')}
          </div>
        ),
      },
      {
        accessorKey: 'userAgent',
        header: 'Device & OS',
        cell: ({ row }) => {
          const result = new UAParser(row.original.userAgent).getResult();
          const device = result.device.model ?? result.device.type ?? 'Desktop';
          const browser = result.browser.name ?? 'Unknown';
          const os = result.os.name ?? 'Unknown';

          return (
            <div className={styles.deviceInfo}>
              <div className={styles.deviceType}>{device}</div>
              <div className={styles.deviceDetails}>
                {browser} {result.browser.version}
              </div>
              <div className={styles.osInfo}>{os}</div>
            </div>
          );
        },
      },
      {
        accessorKey: 'ip',
        header: 'IP & Location',
        cell: ({ row }) => (
          <div className={styles.ipLocationInfo}>
            <div className={styles.ipInfo}>{row.original.ip}</div>
            <div className={styles.locationInfo}>{row.original.location}</div>
          </div>
        ),
      },

      {
        accessorKey: 'endAt',
        header: 'Ends at',
        cell: ({ row }) => (
          <div className={styles.dateInfo}>
            {row.original.endAt
              ? format(new Date(row.original.endAt), 'MMM dd, yyyy HH:mm')
              : 'Unknown'}
          </div>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const isEnded = isSessionEnded(row.original);

          return (
            <Badge
              variant={isEnded ? 'secondary' : 'default'}
              className={isEnded ? styles.inactiveBadge : styles.activeBadge}
            >
              {isEnded ? 'Ended' : 'Active'}
            </Badge>
          );
        },
      },
    ],
    []
  );

  const table = useReactTable({
    data: allSessions,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) {
    return (
      <div className={styles.tableContainer}>
        <table className={styles.sessionsTable}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {Array.from({ length: 5 }).map((_, index) => (
              <tr key={index}>
                {columns.map((_, colIndex) => (
                  <td key={colIndex}>
                    <Skeleton className={styles.skeleton} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.error}>
        Error loading sessions: {error.message}
      </div>
    );
  }

  if (allSessions.length === 0) {
    return (
      <div className={styles.noSessions}>
        <p>No sessions found for this user.</p>
      </div>
    );
  }

  return (
    <div>
      <div className={styles.headerActions}>
        <div>
          <Button
            variant='outlined'
            palette='primary'
            size='md'
            onClick={() => endAllSessionsMutation.mutate()}
            disabled={
              endAllSessionsMutation.isPending ||
              allSessions.every(isSessionEnded)
            }
          >
            {endAllSessionsMutation.isPending
              ? 'Ending...'
              : 'End All Sessions'}
          </Button>
        </div>
      </div>
      <div className={styles.tableContainer}>
        <table className={styles.sessionsTable}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row) => (
              <tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            ))}
            {/* Intersection observer target for infinite scroll */}
            {hasNextPage && (
              <tr ref={ref}>
                <td colSpan={columns.length} className={styles.loadingRow}>
                  {isFetchingNextPage ? (
                    <div className={styles.loadingMore}>
                      <Skeleton className={styles.skeleton} />
                      <span>Loading more sessions...</span>
                    </div>
                  ) : null}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
});

function isSessionEnded(session: SessionDto) {
  return session.endAt && new Date(session.endAt) <= new Date();
}
